/* eslint-disable no-console */
/**
 * PostHog Service - Singleton service for managing PostHog feature flags
 * Simplified for admin portal: feature flags only, no event tracking or session recording
 */

import posthog from 'posthog-js';

class PosthogService {
  static instance = null;

  posthogInstance = null;

  initialized = false;

  flagsReady = false;

  flagsReadyPromise = null;

  flagsReadyResolver = null;

  config = null;

  static getInstance() {
    if (!PosthogService.instance) {
      PosthogService.instance = new PosthogService();
    }
    return PosthogService.instance;
  }

  getPosthogInstance() {
    return this.posthogInstance;
  }

  initialize(config) {
    if (this.initialized || config.disabled || !config.apiKey) {
      if (config.debug) {
        console.log('[PostHog] Initialization skipped', {
          initialized: this.initialized,
          disabled: config.disabled,
          hasApiKey: !!config.apiKey,
        });
      }
      return;
    }

    this.config = config;

    try {
      posthog.init(config.apiKey, {
        api_host: config.apiHost || 'https://us.i.posthog.com',
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        disable_session_recording: true,
        disable_surveys: true,
        enable_heatmaps: false,
        debug: config.debug,
        persistence: 'localStorage+cookie',
        cross_subdomain_cookie: true,
      });

      posthog.register({
        environment: process.env.REACT_APP_TEMP_MEE_ENV || 'development',
        appType: 'admin_portal',
      });

      posthog.people.set({
        environment: process.env.REACT_APP_TEMP_MEE_ENV || 'development',
        appType: 'admin_portal',
      });

      this.posthogInstance = posthog;
      window.posthog = posthog;
      this.initialized = true;

      this.flagsReadyPromise = new Promise((resolve) => {
        this.flagsReadyResolver = resolve;
      });

      this.posthogInstance.onFeatureFlags(() => {
        this.flagsReady = true;
        if (this.flagsReadyResolver) {
          this.flagsReadyResolver();
          this.flagsReadyResolver = null;
        }
        if (config.debug) {
          console.log('[PostHog] Feature flags loaded and ready');
        }
      });
    } catch (error) {
      console.error('[PostHog] Initialization error:', error);
    }
  }

  identify(userId, traits) {
    if (!this.isReady()) return;

    try {
      this.posthogInstance.identify(userId, traits || {});

      if (this.config?.debug) {
        console.log('[PostHog] User identified:', userId, traits);
      }
    } catch (error) {
      console.error('[PostHog] Identify error:', error);
    }
  }

  reset() {
    if (!this.isReady()) return;

    try {
      this.posthogInstance.reset();

      if (this.config?.debug) {
        console.log('[PostHog] User reset');
      }
    } catch (error) {
      console.error('[PostHog] Reset error:', error);
    }
  }

  isFeatureEnabled(flagKey, defaultValue = false) {
    if (!this.isReady()) return defaultValue;

    try {
      const isEnabled = this.posthogInstance.isFeatureEnabled(flagKey);

      if (this.config?.debug) {
        console.log(`[PostHog] Feature flag '${flagKey}':`, isEnabled);
      }

      return isEnabled ?? defaultValue;
    } catch (error) {
      console.error('[PostHog] Feature flag error:', error);
      return defaultValue;
    }
  }

  getFeatureFlagVariant(flagKey) {
    if (!this.isReady()) return null;

    try {
      return this.posthogInstance.getFeatureFlag(flagKey) ?? null;
    } catch (error) {
      console.error('[PostHog] Feature flag variant error:', error);
      return null;
    }
  }

  getFeatureFlagPayload(flagKey) {
    if (!this.isReady()) return null;

    try {
      return this.posthogInstance.getFeatureFlagPayload(flagKey);
    } catch (error) {
      console.error('[PostHog] Feature flag payload error:', error);
      return null;
    }
  }

  reloadFeatureFlags() {
    if (!this.isReady()) return;

    try {
      this.posthogInstance.reloadFeatureFlags();

      if (this.config?.debug) {
        console.log('[PostHog] Feature flags reloaded');
      }
    } catch (error) {
      console.error('[PostHog] Reload feature flags error:', error);
    }
  }

  onFeatureFlags(callback) {
    if (!this.isReady()) return;

    try {
      this.posthogInstance.onFeatureFlags(callback);
    } catch (error) {
      console.error('[PostHog] Feature flags callback error:', error);
    }
  }

  areFlagsReady() {
    return this.flagsReady && this.isReady();
  }

  async waitForFlags() {
    if (!this.isReady()) {
      if (this.config?.disabled) {
        return;
      }
      throw new Error('[PostHog] Service not initialized');
    }

    if (this.flagsReady) {
      return;
    }

    if (this.flagsReadyPromise) {
      await this.flagsReadyPromise;
    }
  }

  isReady() {
    const ready = this.initialized && this.posthogInstance !== null && !this.config?.disabled;

    if (!ready && this.config?.debug) {
      console.log('[PostHog] Not ready:', {
        initialized: this.initialized,
        hasInstance: this.posthogInstance !== null,
        disabled: this.config?.disabled,
      });
    }

    return ready;
  }
}

export default PosthogService;
